<template>
  <div
    role="alert"
    class="flex flex-col items-center justify-center rounded-theme-md text-center"
    :class="[compact ? 'gap-2 px-4 py-4' : 'gap-4 px-6 py-10', containerClass]"
  >
    <!-- Icon -->
    <div
      class="flex items-center justify-center rounded-full bg-theme-palette-red/10 dark:bg-theme-palette-red/20"
      :class="compact ? 'h-9 w-9' : 'h-12 w-12'"
    >
      <svg
        class="text-theme-palette-red dark:text-theme-palette-red"
        :class="compact ? 'h-5 w-5' : 'h-6 w-6'"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
    </div>

    <!-- Title -->
    <div>
      <h3
        class="font-semibold text-theme-text"
        :class="compact ? 'text-sm' : 'text-base'"
      >
        {{ title || $t('general_error') }}
      </h3>
      <p
        v-if="message"
        class="mt-1 text-theme-text-secondary"
        :class="compact ? 'text-xs' : 'text-sm'"
      >
        {{ message }}
      </p>
    </div>

    <!-- Slot for additional content / actions -->
    <slot>
      <button
        v-if="showRetry"
        type="button"
        class="inline-flex items-center gap-1.5 rounded-full border border-theme-palette-red/40 bg-theme-surface px-4 py-1.5 text-sm font-medium text-theme-palette-red transition hover:bg-theme-palette-red/10 dark:border-theme-palette-red dark:bg-transparent dark:text-theme-palette-red dark:hover:bg-theme-palette-red/20"
        @click="$emit('retry')"
      >
        {{ $t('general_try_again') }}
      </button>
    </slot>
  </div>
</template>

<script>
/**
 * AppErrorState — generic error / failure state component.
 *
 * Usage:
 *   <app-error-state :title="t('error.title')" :message="err.message" show-retry @retry="reload" />
 *
 * Or with custom actions via slot:
 *   <app-error-state :title="t('error.title')">
 *     <app-button @click="goHome">Back to home</app-button>
 *   </app-error-state>
 */
export default {
  name: 'AppErrorState',

  props: {
    /** Bold heading text */
    title: { type: String, default: '' },
    /** Descriptive sub-text (error detail) */
    message: { type: String, default: '' },
    /** Tighter padding + smaller icon, for use inline inside a card body */
    compact: { type: Boolean, default: false },
    /** Show the default retry button (ignored if default slot is used) */
    showRetry: { type: Boolean, default: false },
    /** Extra classes on the outer container */
    containerClass: { type: [String, Array, Object], default: '' },
  },

  emits: ['retry'],
};
</script>
